import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import type {
  BlogAnalyticsFilters,
  BlogAnalyticsSummary,
} from "screens/Admin/types";
import { getRelativeFontSize, mediumFont } from "utils/styles";
import blogAnalyticsSectionStyles from "./BlogAnalyticsSection.styles";

const DEFAULT_FILTERS: BlogAnalyticsFilters = {
  dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10),
  dateTo: new Date().toISOString().slice(0, 10),
  category: null,
};

const CATEGORIES = [
  "Hydroponics",
  "Technology",
  "Innovation",
  "Wellness",
  "Finance",
  "Philanthropy",
  "Entrepreneurship",
];

const MOCK_ANALYTICS: BlogAnalyticsSummary = {
  totalBlogs: 24,
  draftCount: 5,
  publishedCount: 16,
  generatingCount: 3,
  blogOpenRate: 42.5,
  avgTimeSpentSeconds: 185,
  likeCount: 1240,
  dislikeCount: 32,
  linkClickThroughRate: 8.2,
  bounceRate: 38,
  scrollDepthPercent: 72,
  uniqueVisitors: 5420,
  returningVisitors: 1890,
  commentsCount: 312,
  engagementScore: 78,
};

const StatCard = ({
  label,
  value,
  suffix = "",
  delay = 0,
}: {
  label: string;
  value: string | number;
  suffix?: string;
  delay?: number;
}) => {
  const theme = useTheme();
  const styles = blogAnalyticsSectionStyles;
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay }}
      >
        <Box sx={styles.card}>
          <Typography
            sx={{
              ...mediumFont,
              fontSize: getRelativeFontSize(0),
              color: "text.secondary",
              mb: 1,
            }}
          >
            {label}
          </Typography>
          <Typography sx={styles.statValue}>
            {typeof value === "number" && Number.isFinite(value)
              ? value.toLocaleString()
              : value}
            {suffix}
          </Typography>
        </Box>
      </motion.div>
    </Grid>
  );
};

const BlogAnalyticsSection = () => {
  const theme = useTheme();
  const styles = blogAnalyticsSectionStyles;
  const [filters, setFilters] = useState<BlogAnalyticsFilters>(DEFAULT_FILTERS);
  const [data, setData] = useState<BlogAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // const result = await fetchBlogAnalytics(filters);
      // setData(result);
    } catch (e: any) {
      setError(null);
      setData(MOCK_ANALYTICS);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <Box id="analytics" sx={styles.section}>
        <Box sx={styles.emptyState}>
          <CircularProgress size={48} sx={{ color: "#f8481c" }} />
          <Typography sx={{ mt: 2 }}>Loading analytics...</Typography>
        </Box>
      </Box>
    );
  }

  const d = data as BlogAnalyticsSummary | null;
  const summary = d ?? {
    totalBlogs: 0,
    draftCount: 0,
    publishedCount: 0,
    generatingCount: 0,
    blogOpenRate: 0,
    avgTimeSpentSeconds: 0,
    likeCount: 0,
    dislikeCount: 0,
    linkClickThroughRate: 0,
    bounceRate: 0,
    scrollDepthPercent: 0,
    uniqueVisitors: 0,
    returningVisitors: 0,
    commentsCount: 0,
    engagementScore: 0,
  };

  return (
    <Box id="analytics" sx={styles.section}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Blog Analytics
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Date from"
            type="date"
            value={filters.dateFrom}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateFrom: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            label="Date to"
            type="date"
            value={filters.dateTo}
            onChange={(e) =>
              setFilters((f) => ({ ...f, dateTo: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
            sx={{ width: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={filters.category ?? ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, category: e.target.value || null }))
              }
            >
              <MenuItem value="">All</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <StatCard label="Total blogs" value={summary.totalBlogs} delay={0} />
        <StatCard label="Draft" value={summary.draftCount} delay={0.05} />
        <StatCard
          label="Published"
          value={summary.publishedCount}
          delay={0.1}
        />
        <StatCard
          label="Generating"
          value={summary.generatingCount}
          delay={0.15}
        />
        <StatCard
          label="Blog open rate"
          value={summary.blogOpenRate}
          suffix="%"
          delay={0.2}
        />
        <StatCard
          label="Avg. time spent"
          value={summary.avgTimeSpentSeconds}
          suffix="s"
          delay={0.25}
        />
        <StatCard label="Likes" value={summary.likeCount} delay={0.3} />
        <StatCard label="Dislikes" value={summary.dislikeCount} delay={0.35} />
        <StatCard
          label="Link CTR"
          value={summary.linkClickThroughRate}
          suffix="%"
          delay={0.4}
        />
        <StatCard
          label="Bounce rate"
          value={summary.bounceRate}
          suffix="%"
          delay={0.45}
        />
        <StatCard
          label="Scroll depth"
          value={summary.scrollDepthPercent}
          suffix="%"
          delay={0.5}
        />
        <StatCard
          label="Unique visitors"
          value={summary.uniqueVisitors}
          delay={0.55}
        />
        <StatCard
          label="Returning visitors"
          value={summary.returningVisitors}
          delay={0.6}
        />
        <StatCard label="Comments" value={summary.commentsCount} delay={0.65} />
        <StatCard
          label="Engagement score"
          value={summary.engagementScore}
          delay={0.7}
        />
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Overview chart
        </Typography>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <Box sx={styles.chartContainer}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: 2,
                height: "100%",
                pt: 2,
              }}
            >
              {[
                { label: "Draft", value: summary.draftCount, color: "#9e9e9e" },
                {
                  label: "Published",
                  value: summary.publishedCount,
                  color: "#f8481c",
                },
                {
                  label: "Generating",
                  value: summary.generatingCount,
                  color: "#ff9800",
                },
              ].map((item, i) => {
                const max = Math.max(
                  summary.draftCount,
                  summary.publishedCount,
                  summary.generatingCount,
                  1,
                );
                const heightPercent = (item.value / max) * 100;
                return (
                  <Box
                    key={item.label}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
                    >
                      {item.label}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 80,
                        height: `${Math.max(heightPercent, 8)}%`,
                        minHeight: 24,
                        borderRadius: "8px 8px 0 0",
                        bgcolor: item.color,
                        transition: "height 0.5s ease",
                      }}
                    />
                    <Typography sx={{ fontSize: 11, mt: 0.5, fontWeight: 600 }}>
                      {item.value}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default BlogAnalyticsSection;
